import { ReactElement, useMemo, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { v4 } from 'uuid'
import { useGetChatStatus } from '@api/agent-runs/agent-runs'
import { useSnackbar } from '@ui/snackbar'
import { CopilotProvider } from 'src/components/contexts'
import useUIStore from 'src/state/stores/ui'
import useSeesChatInternals from '../hooks/useSeesChatInternals'
import { chatModelOptions } from '../modelAnimals'
import {
  AIChatRunProperties,
  approvalModeFor,
  chatUnavailableReason,
  resolveSelectedModel,
} from '../utils'
import AIChatWidgetContent from './AIChatWidgetContent'
import DisabledChatButton from './DisabledChatButton'

interface Props {
  projectId: string
}

/**
 * The chat session of one project: which thread is open, the model it runs
 * with and whether its changes are approved automatically. From these come
 * the run properties, handed to this project's CopilotKit provider as its
 * `properties`, so every run is authorized for exactly the project, mode and
 * model shown (mounted per project, `key={projectId}` in the parent).
 */
const AIChatSession = ({ projectId }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const storedThreadId = useUIStore(state => state.aiChatThreadIdByProject[projectId])
  // A fresh id per mount, used until the session store holds one for this
  // project; its first turn stores it (`useRememberThread`), so the chat
  // comes back to it.
  const [generatedThreadId] = useState(() => v4())
  const threadId = storedThreadId ?? generatedThreadId

  // The model catalogue (`AGENT_MODELS` on the API) and which entry this
  // thread runs with. The pick is session state per thread; a reopened
  // thread starts from the model of its latest turn (passed by the menu).
  const storedModel = useUIStore(state => state.aiChatModelByThread[threadId])
  const autoApprove = useUIStore(state => state.aiChatAutoApproveByThread[threadId] ?? false)
  const { data: status } = useGetChatStatus(
    { project_guid: projectId },
    {
      query: {
        staleTime: Infinity,
        onError: (error: AxiosError) => {
          enqueueSnackbar(
            buildErrorMessage(error, 'KI-Chat: Modelle konnten nicht geladen werden'),
            { variant: 'error' },
          )
        },
      },
    },
  )
  // Users who are neither developers nor admins pick animals, never a model's real name.
  const seesInternals = useSeesChatInternals()
  const models = useMemo(
    () => chatModelOptions(status?.models ?? [], seesInternals),
    [status, seesInternals],
  )
  const selectedModel = resolveSelectedModel(models, storedModel, status?.default_model)

  const runProperties = useMemo(
    (): AIChatRunProperties => ({
      projectGuid: projectId,
      approvalMode: approvalModeFor(autoApprove),
      model: selectedModel,
    }),
    [projectId, autoApprove, selectedModel],
  )

  // The API knows whether this environment can run the chat at all and how
  // much of the organisation's monthly quota is left; a turn would be
  // refused otherwise, so the button says why up front.
  const unavailableReason = status ? chatUnavailableReason(status) : null
  if (unavailableReason) return <DisabledChatButton reason={unavailableReason} />

  return (
    <CopilotProvider
      properties={runProperties}
      fallback={<DisabledChatButton reason="KI-Chat wird verbunden …" />}
    >
      <AIChatWidgetContent
        projectId={projectId}
        threadId={threadId}
        models={models}
        defaultModel={status?.default_model}
        selectedModel={selectedModel}
        autoApprove={autoApprove}
        runProperties={runProperties}
      />
    </CopilotProvider>
  )
}

export default AIChatSession
